/**
 * Goals State
 *
 * NGXS state for managing goals data with pagination.
 * Stores goals loaded from the partners/{partnerId}/goals subcollection
 * and provides selectors for accessing goal data throughout the application.
 */

import { Injectable } from '@angular/core';
import {
  State,
  Action,
  StateContext,
  Selector,
  NgxsOnInit,
  Store,
} from '@ngxs/store';
import { Goal } from 'src/app/models/schema';
import {
  InitializeGoals,
  InitializeGoalsSuccess,
  InitializeGoalsFailure,
  LoadNextBatch,
  LoadNextBatchSuccess,
  LoadNextBatchFailure,
  SetGoalsFilters,
  SetGoalsSort,
  AddGoal,
  AddGoalSuccess,
  AddGoalFailure,
  UpdateGoal,
  UpdateGoalSuccess,
  UpdateGoalFailure,
  DeleteGoal,
  DeleteGoalSuccess,
  DeleteGoalFailure,
  UpdateGoalsFromListener,
  ClearGoals,
  ClearGoalsError,
} from './goals.actions';
import { ActiveTeamState } from 'src/app/rxjs-store/account/active-team';
import { AuthState } from 'src/app/store/auth';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Goals State Model
 *
 * State shape for the goals store
 */
export interface GoalsStateModel {
  /** Array of goal entries */
  goals: Goal[];
  /** Whether goals are currently loading */
  loading: boolean;
  /** Whether the store has been initialized */
  initialized: boolean;
  /** Error message if an operation failed */
  error: string | null;
  /** Whether there are more goals to load */
  hasMore: boolean;
  /** Current page number (for tracking pagination) */
  currentPage: number;
  /** Current partner ID (for tracking which team's goals are loaded) */
  partnerId: string | null;
  /** Current filter criteria */
  filters: any;
  /** Current sort field */
  sortField: string;
  /** Current sort direction */
  sortDirection: 'asc' | 'desc';
}

/**
 * Default state values
 */
const defaults: GoalsStateModel = {
  goals: [],
  loading: false,
  initialized: false,
  error: null,
  hasMore: true,
  currentPage: 0,
  partnerId: null,
  filters: {},
  sortField: 'createdAt',
  sortDirection: 'desc',
};

/**
 * Goals State
 *
 * Manages goals data in the NGXS store.
 * Automatically resets when the active team changes or when user signs out.
 */
@State<GoalsStateModel>({
  name: 'goals',
  defaults,
})
@Injectable()
export class GoalsState implements NgxsOnInit {
  constructor(private store: Store) {}

  ngxsOnInit(ctx: StateContext<GoalsStateModel>): void {
    let previousAuthState: boolean | null = null;

    // Subscribe to auth state changes - clear on sign out
    this.store
      .select(AuthState.isAuthenticated)
      .pipe(distinctUntilChanged())
      .subscribe((isAuthenticated: boolean) => {
        // Only clear if we're transitioning from authenticated to unauthenticated
        // (not on initial load where auth state might not be hydrated yet)
        if (previousAuthState === true && !isAuthenticated) {
          ctx.setState(defaults);
          console.log('[GoalsState] State cleared - user signed out');
        }

        // Track previous auth state
        previousAuthState = isAuthenticated;
      });

    // Subscribe to active team changes - reset state when team changes
    this.store
      .select(ActiveTeamState.activeTeamId)
      .pipe(distinctUntilChanged())
      .subscribe((activeTeamId) => {
        const currentState = ctx.getState();
        const currentPartnerId = currentState.partnerId;

        // Only clear if the activeTeamId has genuinely changed AND the state was previously loaded
        if (activeTeamId !== currentPartnerId && currentState.initialized) {
          ctx.setState(defaults);
          console.log(
            `[GoalsState] Active team changed from ${currentPartnerId} to ${activeTeamId} - clearing goals`
          );
        }
      });
  }

  // ========================================
  // Selectors
  // ========================================

  /**
   * Select the entire goals state
   */
  @Selector()
  static getState(state: GoalsStateModel): GoalsStateModel {
    return state;
  }

  /**
   * Select all goals
   */
  @Selector()
  static goals(state: GoalsStateModel): Goal[] {
    return state.goals;
  }

  /**
   * Select goals sorted by creation date (most recent first)
   */
  @Selector()
  static goalsSorted(state: GoalsStateModel): Goal[] {
    return [...state.goals].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }

  /**
   * Select loading state
   */
  @Selector()
  static loading(state: GoalsStateModel): boolean {
    return state.loading;
  }

  /**
   * Select initialized state
   */
  @Selector()
  static initialized(state: GoalsStateModel): boolean {
    return state.initialized;
  }

  /**
   * Select error state
   */
  @Selector()
  static error(state: GoalsStateModel): string | null {
    return state.error;
  }

  /**
   * Select hasMore pagination state
   */
  @Selector()
  static hasMore(state: GoalsStateModel): boolean {
    return state.hasMore;
  }

  /**
   * Select current partner ID
   */
  @Selector()
  static partnerId(state: GoalsStateModel): string | null {
    return state.partnerId;
  }

  /**
   * Select current filters
   */
  @Selector()
  static filters(state: GoalsStateModel): any {
    return state.filters;
  }

  /**
   * Select current sort configuration
   */
  @Selector()
  static sort(state: GoalsStateModel): {
    field: string;
    direction: 'asc' | 'desc';
  } {
    return {
      field: state.sortField,
      direction: state.sortDirection,
    };
  }

  // ========================================
  // Actions
  // ========================================

  /**
   * Initialize goals - load first batch
   */
  @Action(InitializeGoals)
  async initializeGoals(
    ctx: StateContext<GoalsStateModel>,
    action: InitializeGoals
  ) {
    ctx.patchState({
      loading: true,
      error: null,
      partnerId: action.partnerId,
    });
  }

  /**
   * Handle successful initialization
   */
  @Action(InitializeGoalsSuccess)
  initializeGoalsSuccess(
    ctx: StateContext<GoalsStateModel>,
    action: InitializeGoalsSuccess
  ) {
    ctx.patchState({
      goals: action.goals,
      hasMore: action.hasMore,
      loading: false,
      initialized: true,
      currentPage: 1,
      error: null,
    });
  }

  /**
   * Handle failed initialization
   */
  @Action(InitializeGoalsFailure)
  initializeGoalsFailure(
    ctx: StateContext<GoalsStateModel>,
    action: InitializeGoalsFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Load next batch of goals
   */
  @Action(LoadNextBatch)
  async loadNextBatch(ctx: StateContext<GoalsStateModel>) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Handle successful next batch load
   */
  @Action(LoadNextBatchSuccess)
  loadNextBatchSuccess(
    ctx: StateContext<GoalsStateModel>,
    action: LoadNextBatchSuccess
  ) {
    const state = ctx.getState();
    ctx.patchState({
      goals: [...state.goals, ...action.goals],
      hasMore: action.hasMore,
      loading: false,
      currentPage: state.currentPage + 1,
      error: null,
    });
  }

  /**
   * Handle failed next batch load
   */
  @Action(LoadNextBatchFailure)
  loadNextBatchFailure(
    ctx: StateContext<GoalsStateModel>,
    action: LoadNextBatchFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Set filter criteria
   */
  @Action(SetGoalsFilters)
  setFilters(ctx: StateContext<GoalsStateModel>, action: SetGoalsFilters) {
    ctx.patchState({
      filters: action.filters,
    });
  }

  /**
   * Set sort criteria
   */
  @Action(SetGoalsSort)
  setSort(ctx: StateContext<GoalsStateModel>, action: SetGoalsSort) {
    ctx.patchState({
      sortField: action.sortField,
      sortDirection: action.sortDirection,
    });
  }

  /**
   * Add a new goal
   */
  @Action(AddGoal)
  async addGoal(ctx: StateContext<GoalsStateModel>, action: AddGoal) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Handle successful goal add
   */
  @Action(AddGoalSuccess)
  addGoalSuccess(ctx: StateContext<GoalsStateModel>, action: AddGoalSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      goals: [action.goal, ...state.goals],
      loading: false,
      error: null,
    });
  }

  /**
   * Handle failed goal add
   */
  @Action(AddGoalFailure)
  addGoalFailure(ctx: StateContext<GoalsStateModel>, action: AddGoalFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Update an existing goal
   */
  @Action(UpdateGoal)
  async updateGoal(ctx: StateContext<GoalsStateModel>, action: UpdateGoal) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Handle successful goal update
   */
  @Action(UpdateGoalSuccess)
  updateGoalSuccess(
    ctx: StateContext<GoalsStateModel>,
    action: UpdateGoalSuccess
  ) {
    const state = ctx.getState();
    const updatedGoals = state.goals.map((goal) =>
      goal.id === action.goal.id ? action.goal : goal
    );
    ctx.patchState({
      goals: updatedGoals,
      loading: false,
      error: null,
    });
  }

  /**
   * Handle failed goal update
   */
  @Action(UpdateGoalFailure)
  updateGoalFailure(
    ctx: StateContext<GoalsStateModel>,
    action: UpdateGoalFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Delete a goal
   */
  @Action(DeleteGoal)
  async deleteGoal(ctx: StateContext<GoalsStateModel>, action: DeleteGoal) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Handle successful goal deletion
   */
  @Action(DeleteGoalSuccess)
  deleteGoalSuccess(
    ctx: StateContext<GoalsStateModel>,
    action: DeleteGoalSuccess
  ) {
    const state = ctx.getState();
    const filteredGoals = state.goals.filter(
      (goal) => goal.id !== action.goalId
    );
    ctx.patchState({
      goals: filteredGoals,
      loading: false,
      error: null,
    });
  }

  /**
   * Handle failed goal deletion
   */
  @Action(DeleteGoalFailure)
  deleteGoalFailure(
    ctx: StateContext<GoalsStateModel>,
    action: DeleteGoalFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Update goals from real-time listener
   */
  @Action(UpdateGoalsFromListener)
  updateGoalsFromListener(
    ctx: StateContext<GoalsStateModel>,
    action: UpdateGoalsFromListener
  ) {
    ctx.patchState({
      goals: action.goals,
    });
  }

  /**
   * Clear all goals data
   */
  @Action(ClearGoals)
  clearGoals(ctx: StateContext<GoalsStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Clear error state
   */
  @Action(ClearGoalsError)
  clearError(ctx: StateContext<GoalsStateModel>) {
    ctx.patchState({
      error: null,
    });
  }
}
